import * as React from "react"
import {Card, CardContent,} from "@/components/ui/card"
import {Input} from "@/components/ui/input"
import {Label} from "@/components/ui/label"
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue,} from "@/components/ui/select"
import {LLMModel, LLMOperator,} from "@/model/Workflow";
import {GPTPrompt, GPTPromptRoleType, LlmConfig} from "@/model/llmSpecification";
import {Textarea} from "@/components/ui/textarea";
import {OperatorDetailProps} from "@/view/workflow/components/operatorForm/index";
import Editor from "@monaco-editor/react";
import _ from "lodash";
import {useMemo, useState} from "react";
import useLlmConfig from "@/query/llmConfig/useLlmConfig";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/components/ui/accordion";

const config_editor_options = {
    minimap: {enabled: false},
    lineNumbers: "on",
    wordWrap: "on",
    scrollBeyondLastLine: false,
    fontSize: 12,
    tabSize: 2,
    automaticLayout: true,
};

interface LLMOperatorFormProps extends OperatorDetailProps {
    operator: LLMOperator;
    syncWorkflow?: () => void;
}

export const LLMOperatorForm: React.FC<LLMOperatorFormProps> = (props) => {
    const {operator, readOnly, syncWorkflow} = props;

    const {data: llmConfig} = useLlmConfig();
    const [vendor, setVendor] = useState<string>(operator.model.vendor);
    const [model, setModel] = useState<string>(operator.model.model);

    const modelsByProvider = useMemo(() =>
            Object.fromEntries((llmConfig ?? []).map((config: LlmConfig) => [config.provider, config.models])
            ) as Record<string, string[]>,
        [llmConfig]
    );

    const onChangeVendor = (vendor: string) => {
        setVendor(vendor);
        operator.model.vendor = vendor;

        const firstModel = modelsByProvider[vendor]?.[0] ?? "";
        if (firstModel) {
            setModel(firstModel);
            operator.model.model = firstModel;
        }
        syncWorkflow && syncWorkflow();
    }

    const onChangeModel = (model: string) => {
        setModel(model);
        operator.model.model = model;
        syncWorkflow && syncWorkflow();
    }

    return (
        <div className="grid w-full items-center gap-4 h-full">
            <Card className='p-6'>
                <CardContent className="grid gap-4">
                    <div className="flex flex-col space-y-1.5">
                        <Label htmlFor="framework">LLM Providers</Label>
                        <Select value={vendor} onValueChange={onChangeVendor} disabled={readOnly}>
                            <SelectTrigger id="framework">
                                <SelectValue placeholder="Select"/>
                            </SelectTrigger>
                            <SelectContent position="popper">
                                {llmConfig?.map((llm) => (
                                    <SelectItem key={llm.provider} value={llm.provider}>{llm.provider}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex flex-col space-y-1.5">
                        <Label htmlFor="model">LLM Model</Label>
                        <Select value={model} onValueChange={onChangeModel} disabled={readOnly}>
                            <SelectTrigger id="model">
                                <SelectValue placeholder="Select"/>
                            </SelectTrigger>
                            <SelectContent position="popper">
                                {modelsByProvider[vendor]?.map((modelItem) => (
                                    <SelectItem key={modelItem} value={modelItem}>{modelItem}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <div className="flex flex-col space-y-1.5">
                <Label htmlFor="framework">Prompts</Label>

                <div className='flex flex-col gap-2 mt-3'>
                    {operator.prompts.map((prompt, index) => (
                        <PromptForm key={`prompt_${index}`} prompt={prompt} readonly={readOnly}/>
                    ))}
                </div>
            </div>

            <div className="flex flex-col space-y-1.5">
                <Label htmlFor="framework">LLM Config</Label>
                <LlmConfigForm operator={operator}
                               readOnly={readOnly}
                />
            </div>
        </div>
    )
}

interface PromptFormProps {
    prompt: GPTPrompt;
    readonly?: boolean;
}

const PromptForm: React.FC<PromptFormProps> = (props) => {
    const {prompt, readonly} = props;

    const onChangeContent = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        prompt.content = e.target.value
    }

    const onChangeRole = (role: GPTPromptRoleType) => {
        prompt.role = role
    }
    return (
        <Card className="p-6">
            <CardContent>
                <div className="grid w-full items-center gap-4">
                    <div className="flex flex-col space-y-1.5">
                        <Label htmlFor="framework">Role</Label>
                        <Select defaultValue={prompt.role} onValueChange={onChangeRole} disabled={readonly}>
                            <SelectTrigger id="framework">
                                <SelectValue placeholder="Select"/>
                            </SelectTrigger>
                            <SelectContent position="popper">
                                {Object.values(GPTPromptRoleType).map((type) => (
                                    <SelectItem key={type} value={type}>{type}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex flex-col space-y-1.5">
                        <Label htmlFor="name">Content</Label>
                        <Textarea placeholder="Type your message here."
                                  rows={15}
                                  disabled={readonly}
                                  defaultValue={prompt.content}
                                  onChange={onChangeContent}/>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

function parseJsonArray(value: string): object[] | [] {
    try {
        const parsed: unknown = JSON.parse(value)
        if (!Array.isArray(parsed)) {
            return []
        }
        return parsed as object[]
    } catch {
        return []
    }
}

interface LlmConfigFormProps {
    operator: LLMOperator;
    readOnly?: boolean;
}

const LlmConfigForm: React.FC<LlmConfigFormProps> = (props) => {
    const {operator, readOnly} = props;

    const onChangeFunctionFormat = useMemo(
        () => _.debounce((value?: string) => {
            if (!value) {
                operator.functionFormat = []
                return;
            }

            const parsed = parseJsonArray(value)
            if (!parsed)
                return

            operator.functionFormat = parsed
        }, 300),
        [operator],
    )

    const onChangeTruncatedOnTokenLimit = useMemo(
        () => _.debounce((value?: string) => {
            if (!value) {
                operator.truncatedOnTokenLimit = []
                return;
            }

            const parsed = parseJsonArray(value)
            if (!parsed)
                return

            operator.truncatedOnTokenLimit = parsed
        }, 300),
        [operator],
    )

    const functionFormatCount = Array.isArray(operator.functionFormat) ? operator.functionFormat.length : 0
    const truncatedCount = Array.isArray(operator.truncatedOnTokenLimit) ? operator.truncatedOnTokenLimit.length : 0

    return (
        <Card className="overflow-hidden">
            <CardContent className="p-4">
                <Accordion type="multiple" defaultValue={["function-format"]} className="w-full">
                    <AccordionItem value="function-format" className="border-b px-1">
                        <AccordionTrigger className="py-3 text-sm hover:no-underline">
                            <span className="flex items-center gap-2">
                                Function Format
                                <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                                    {functionFormatCount}
                                </span>
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-3">
                            <p className="mb-2 text-xs text-muted-foreground">
                                JSON 배열만 저장됩니다.
                            </p>
                            <div className="overflow-hidden rounded-md border border-input">
                                <Editor
                                    key={`functionFormat-${operator._id}`}
                                    height={180}
                                    className={readOnly ? "opacity-50" : ""}
                                    defaultLanguage="json"
                                    defaultValue={JSON.stringify(
                                        Array.isArray(operator.functionFormat) ? operator.functionFormat : [],
                                        null,
                                        2,
                                    )}
                                    options={{...config_editor_options, readOnly}}
                                    onChange={onChangeFunctionFormat}
                                />
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="truncated-on-token-limit" className="border-b px-1">
                        <AccordionTrigger className="py-3 text-sm hover:no-underline">
                            <span className="flex items-center gap-2">
                                Truncated On Token Limit
                                <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                                    {truncatedCount}
                                </span>
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-3">
                            <p className="mb-2 text-xs text-muted-foreground">
                                토큰 한도 초과 시 잘라낼 컨텍스트 규칙입니다.
                            </p>
                            <div className="overflow-hidden rounded-md border border-input">
                                <Editor
                                    key={`truncatedOnTokenLimit-${operator._id}`}
                                    height={180}
                                    className={readOnly ? "opacity-50" : ""}
                                    defaultLanguage="json"
                                    defaultValue={JSON.stringify(
                                        Array.isArray(operator.truncatedOnTokenLimit) ? operator.truncatedOnTokenLimit : [],
                                        null,
                                        2,
                                    )}
                                    options={{...config_editor_options, readOnly}}
                                    onChange={onChangeTruncatedOnTokenLimit}
                                />
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="model-params" className="border-none px-1">
                        <AccordionTrigger className="py-3 text-sm hover:no-underline">
                            Model Parameters
                        </AccordionTrigger>
                        <AccordionContent className="pb-1">
                            <ParameterForm key={`model-params-${operator._id}`}
                                           llmModel={operator.model}
                                           readOnly={readOnly}
                            />
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </CardContent>
        </Card>
    )
}

interface ParameterFormProps {
    llmModel: LLMModel;
    readOnly?: boolean;
}

const ParameterForm: React.FC<ParameterFormProps> = (props) => {
    const {llmModel, readOnly} = props;

    const onChangeContextWindowSize = (e: React.ChangeEvent<HTMLInputElement>) => {
        llmModel.contextWindowSize = e.target.value ? Number(e.target.value) : 0;
    }

    const onChangeTemperature = (e: React.ChangeEvent<HTMLInputElement>) => {
        llmModel.temperature = e.target.value ? Number(e.target.value) : 0;
    }

    const onChangeMaxTokens = (e: React.ChangeEvent<HTMLInputElement>) => {
        llmModel.maxTokens = e.target.value ? Number(e.target.value) : 0;
    }

    return (
        <div className="grid w-full gap-3">
            <div className="flex flex-col gap-1.5">
                <Label htmlFor="contextWindowSize" className="text-xs text-muted-foreground">
                    Context window Size
                </Label>
                <Input
                    id="contextWindowSize"
                    type="number"
                    defaultValue={llmModel.contextWindowSize}
                    disabled={readOnly}
                    onChange={onChangeContextWindowSize}
                />
            </div>
            <div className="flex flex-col gap-1.5">
                <Label htmlFor="temperature" className="text-xs text-muted-foreground">
                    Temperature
                </Label>
                <Input
                    id="temperature"
                    type="number"
                    step="0.1"
                    defaultValue={llmModel.temperature}
                    disabled={readOnly}
                    onChange={onChangeTemperature}
                />
            </div>
            <div className="flex flex-col gap-1.5">
                <Label htmlFor="maxTokens" className="text-xs text-muted-foreground">
                    Max tokens
                </Label>
                <Input
                    id="maxTokens"
                    type="number"
                    defaultValue={llmModel.maxTokens}
                    disabled={readOnly}
                    onChange={onChangeMaxTokens}
                />
            </div>
        </div>
    )
}

export default LLMOperatorForm
